import { createContext, useContext, type ComponentProps, type ReactNode } from 'react';
import { Drawer, Modal } from '@heroui/react';
import { cn } from '@/shared/lib/cn';
import { useDesktopLayout } from '@/shared/lib/desktopLayout';

/** Внутри окна по центру (компьютер) или шторки снизу (телефон) — для частей ниже. */
const CenteredContext = createContext(false);

interface SheetFrameProps {
  onClose: () => void;
  children: ReactNode;
  /** Классы диалога — отступы снизу и т. п. На компьютере `safe-bottom` ни к чему, но и не мешает. */
  dialogClassName?: string;
}

/**
 * Рамка шторки: на телефоне и в Telegram — HeroUI `Drawer` снизу (свайп
 * вниз, хэндл), на компьютере в браузере — `Modal` по центру. Шторка во
 * всю ширину монитора, выезжающая снизу, — мобильный приём; на ноутбуке
 * ждут окно (WEB.md, десктопная раскладка).
 *
 * Части внутри — `SheetFrameBody`/`SheetFrameHeading`/`SheetFrameFooter`, а не
 * `Drawer.Body` напрямую: части `Drawer` вне `Drawer` ломаются, и наоборот.
 */
export function SheetFrame({ onClose, children, dialogClassName }: SheetFrameProps) {
  const centered = useDesktopLayout();
  const onOpenChange = (open: boolean) => !open && onClose();

  if (centered) {
    return (
      <CenteredContext.Provider value>
        {/* Без modal.css (см. `app/styles/index.css`): раскладка — классами здесь,
            поведение (Esc, клик по фону, ловушка фокуса) — от react-aria внутри `Modal`. */}
        <Modal isOpen onOpenChange={onOpenChange}>
          <Modal.Backdrop
            variant="opaque"
            className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(17,24,39,0.45)] p-6"
          >
            <Modal.Container className="flex max-h-full w-full max-w-[480px] outline-none">
              <Modal.Dialog
                className={cn(
                  'flex max-h-[calc(100dvh-3rem)] w-full flex-col overflow-hidden rounded-[28px] bg-glass px-6 pt-2 pb-6 shadow-[0_24px_64px_-16px_rgba(17,24,39,0.35)] outline-none',
                  dialogClassName,
                )}
              >
                {/* Фрагмент — из-за двух версий `@types/react` в монорепо голый
                    `ReactNode` пропом не проходит проверку типов. */}
                <>{children}</>
              </Modal.Dialog>
            </Modal.Container>
          </Modal.Backdrop>
        </Modal>
      </CenteredContext.Provider>
    );
  }

  return (
    <Drawer isOpen onOpenChange={onOpenChange}>
      <Drawer.Backdrop variant="opaque">
        <Drawer.Content placement="bottom">
          <Drawer.Dialog className={dialogClassName}>
            <Drawer.Handle />
            <>{children}</>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}

/**
 * Тело прокручивается — а `overflow` режет всё, что выходит за края, в том
 * числе рамку фокуса полей ввода (она рисуется снаружи поля, а поле стоит
 * вплотную к краю). Запас в 4 px внутри и тот же сдвиг наружу: содержимое
 * стоит где стояло, рамке есть куда лечь.
 */
const BODY_RING_ROOM = '-mx-1 -my-1 px-1 py-1';

export function SheetFrameBody(props: ComponentProps<typeof Drawer.Body>) {
  return useContext(CenteredContext) ? (
    <Modal.Body {...props} className={cn('min-h-0 flex-1 overflow-y-auto', BODY_RING_ROOM, props.className)} />
  ) : (
    <Drawer.Body {...props} />
  );
}

export function SheetFrameHeading(props: ComponentProps<typeof Drawer.Heading>) {
  return useContext(CenteredContext) ? <Modal.Heading {...props} /> : <Drawer.Heading {...props} />;
}

export function SheetFrameFooter(props: ComponentProps<typeof Drawer.Footer>) {
  return useContext(CenteredContext) ? (
    <Modal.Footer {...props} className={cn('flex shrink-0 gap-2 pt-4', props.className)} />
  ) : (
    <Drawer.Footer {...props} />
  );
}
